import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { promptsApi } from '../services/api';

const PromptResults = () => {
  const { promptid } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDraft, setShowDraft] = useState(false);

  useEffect(() => {
    const loadResults = async () => {
      setLoading(true);
      setError('');

      try {
        const res = await promptsApi.getResults(promptid);
        setPrompt(res.data.prompt);
        setJob(res.data.job || null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load prompt results');
      } finally {
        setLoading(false);
      }
    };

    if (promptid) {
      loadResults();
    }
  }, [promptid]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Prompt Results</h1>
            <p className="mt-2 text-sm text-gray-500">Review the workflow results for this prompt.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/prompts')}
            className="inline-flex items-center rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Back to prompts
          </button>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Prompt name</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{prompt?.name || 'Untitled'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{prompt?.status || 'new'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Prompt text</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{prompt?.prompt_text || prompt?.content || '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Workflow</p>
                  <p className="mt-1 text-sm text-gray-900">{prompt?.workflow_type || 'content_creation'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Submitted by</p>
                  <p className="mt-1 text-sm text-gray-900">{prompt?.created_by || 'Unknown'}</p>
                </div>
              </div>
            </div>

            {!job ? (
              <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                No associated job data was found for this prompt.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Job summary</h2>
                      <p className="text-sm text-gray-500">Latest content job for this prompt.</p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{job.status}</span>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Iteration</p>
                      <p className="mt-1 text-sm text-gray-900">{job.iteration ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Review score</p>
                      <p className="mt-1 text-sm text-gray-900">{job.review_score ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Updated</p>
                      <p className="mt-1 text-sm text-gray-900">{new Date(job.updated_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Created</p>
                      <p className="mt-1 text-sm text-gray-900">{new Date(job.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {job.final_output ? (
                  <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Final output</h3>
                    <pre className="whitespace-pre-wrap text-sm text-gray-700">{job.final_output}</pre>
                  </div>
                ) : job.error_msg ? (
                  <div className="rounded-3xl border border-red-200 bg-red-50 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-red-800 mb-3">Error</h3>
                    <p className="text-sm text-red-700">{job.error_msg}</p>
                  </div>
                ) : null}

                {job.stage_2_output && (
                  <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Draft output</h3>
                        <p className="text-sm text-gray-500">Expand to view the generated draft.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowDraft((prev) => !prev)}
                        className="rounded-full border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {showDraft ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {showDraft ? (
                      <pre className="whitespace-pre-wrap text-sm text-gray-700">{job.stage_2_output}</pre>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                        Draft output is hidden. Expand to view.
                      </div>
                    )}
                  </div>
                )}

                {job.feedback && (
                  <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Review feedback</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.feedback}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptResults;
